import * as React from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Store } from 'redux';
import { Redirect } from 'react-router';
import { RouteComponentProps, match } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { WSAppContextType, WSAppContext } from '@weddingspot/ws-core-components';
import NotFound from '../components/404';
import CuratedListPage from '../components/curated-lists';
import { LandingPagesAction, loadCuratedListPageData } from '../actions/landingPages';
import * as types from '../types';
import { logEvent, AnalyticsEvent } from '@weddingspot/ws-analytics';
import * as constants from '../constants';
import { addServerError } from '../actions/errors';

type RouteMatchProps = {
    market: string;
    region: string;
    bucket: string;
};
type CuratedListsPageStateProps = {
    data: types.BucketLandingPage;
    notFound: boolean;
};

interface BucketRouteProps extends RouteComponentProps<RouteMatchProps> {}

export class CuratedListsPage extends React.Component<
    BucketRouteProps & CuratedListsPageStateProps & ReturnType<typeof mapDispatchToProps>
> {
    readonly state = {};
    static contextType: React.Context<WSAppContextType> = WSAppContext;
    context!: React.ContextType<typeof WSAppContext>;

    static fetchData(store: Store<types.StoreState>, match: match<RouteMatchProps>): Promise<any> {
        // For whatever reason, route match props coming from the express server doesn't decode the URI coponents ahead
        // of time so we have to do it here
        let params = {
            bucket: decodeURIComponent(match.params.bucket.replace(/\+/g, '%20')),
            style: decodeURIComponent(match.params.bucket.replace(/\+/g, '%20')),
        } as any;

        if (!!match.params.market) {
            params.market = decodeURIComponent(match.params.market.replace(/\+/g, '%20'));
        }

        if (!!match.params.region) {
            params.region = decodeURIComponent(match.params.region.replace(/\+/g, '%20'));
        }
        return loadCuratedListPageData(params)(store.dispatch).catch((err) => {
            store.dispatch(addServerError(err.status));
            throw err;
        });
    }

    componentDidMount() {
        this.props
            .load({
                bucket: this.props.match.params.bucket,
                market: this.props.match.params.market,
                region: this.props.match.params.region,
                style: this.props.match.params.bucket,
            })
            .catch((err: any) => {
                console.error(err);
            });
        logEvent(AnalyticsEvent.EVTNM_VISITED_WEDDING_VENUE_LIST_PAGE, {
            Market: this.props.match.params.market,
            Region: this.props.match.params.region,
            Style: this.props.data.style,
        });
    }

    renderList() {
        const user = this.context.user;

        if (this.props.data) {
            return (
                <>
                    {!this.props.data.is_published && !!user && !user.isStaff ? (
                        <NotFound />
                    ) : (
                        <CuratedListPage data={this.props.data} curPath={this.props.match.url} />
                    )}
                </>
            );
        } else {
            return '';
        }
    }

    render() {
        if (this.props.notFound) {
            return <Redirect to='/404/' />;
        }
        const location = this.props.data.region || this.props.data.market;
        let titleString, metaDescription;
        if (location) {
            titleString = `Top ${this.props.data.venue_data.length} ${this.props.data.bucket} Venues in ${location}`;
            metaDescription = `Discover the best ${this.props.data.bucket} wedding venues in ${location} with our guide! Explore each venue & learn why they made our top ${this.props.data.venue_data.length} list.`;
        }
        if (!location) {
            titleString = `Top ${this.props.data.venue_data.length} ${this.props.data.style} Venues`;
            metaDescription = `Discover the best ${this.props.data.style} wedding venues with our guide! Explore each venue & learn why they made our top ${this.props.data.venue_data.length} list.`;
        }
        return (
            <div>
                <Helmet>
                    <title>{titleString}</title>
                    <meta name='description' content={metaDescription} />
                </Helmet>
                {this.renderList()}
            </div>
        );
    }
}

const mapStateToProps = (state: types.StoreState, ownProps: BucketRouteProps) => ({
    data: state.barnVenue,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<types.StoreState, undefined, LandingPagesAction>) => ({
    load: (params: { bucket: string; market: string; region: string; count?: number; style?: string }) =>
        dispatch(loadCuratedListPageData(params)),
});

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(CuratedListsPage);
